'use client';

import React, { useState } from 'react';
import { DropdownItem, DropdownMenu as DropdownMenuType, dropdownMenus } from '@data/links';
import { Button } from '@ui';
import { IconChevronDown } from '@icons';
import { t } from '@utils/translate';
import { renderTranslation } from '@utils/renderTranslation';

/** Landing href for the trigger when no explicit `triggerHref` is passed (e.g. Storybook / legacy menus). */
function deriveTriggerHref(items: DropdownItem[]): string {
  if (!items.length) return '#';
  const first = items[0].href;
  try {
    const u = new URL(first, 'http://example.com');
    const path = u.pathname.replace(/\/+$/, '') || '/';
    const segs = path.split('/').filter(Boolean);
    if (segs.length > 1) return `/${segs.slice(0, -1).join('/')}`;
    if (segs.length === 1) return `/${segs[0]}`;
    return first;
  } catch {
    return first;
  }
}

interface DropdownMenuProps {
  id: string;
  triggerText: string;
  triggerHref: string;
  triggerActive?: boolean;
  tabIndex?: number;
  locale?: 'en' | 'es';
  items: DropdownItem[];
  menuClassName: string;
  isOpen: boolean;
  onToggle: () => void;
  className?: string;
  rootRef?: React.Ref<HTMLDivElement>;
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  id,
  triggerText,
  triggerHref,
  triggerActive,
  tabIndex,
  locale = 'en',
  items,
  menuClassName,
  isOpen,
  onToggle,
  className,
  rootRef,
}) => {
  if (items.length === 0) return null;
  const isWide = menuClassName.includes('wide');

  const toggleAriaLabel = String(
    renderTranslation(t('header.nav_dropdown_toggle_aria', 'global', locale)) || 'Open section menu'
  );

  // Hover handlers
  const handleMouseEnter = () => {
    if (!isOpen) onToggle();
  };
  const handleMouseLeave = () => {
    if (isOpen) onToggle();
  };

  const handleToggleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onToggle();
  };

  return (
    <div
      ref={rootRef}
      className={`nav-item has-dropdown ${isOpen ? 'open' : ''} ${className || ''}`}
      data-dropdown
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className='dropdown-trigger-split'>
        <Button
          as='a'
          href={triggerHref}
          className={`btn btn-link nav-link dropdown-trigger-label${triggerActive ? ' active' : ''}`}
          id={`${id}_label`}
          label={triggerText}
          tabIndex={tabIndex}
        />
        <Button
          type='button'
          id={`${id}_btn`}
          className='btn btn-link nav-link dropdown-trigger-toggle'
          aria-expanded={isOpen}
          aria-haspopup={true}
          aria-controls={`${id}_menu`}
          aria-label={toggleAriaLabel}
          onClick={handleToggleClick}
          icon={<IconChevronDown className='chev' />}
          tabIndex={tabIndex}
        />
      </div>

      <div className={menuClassName} id={`${id}_menu`} role='menu' aria-labelledby={`${id}_label`}>
        {isWide ? (
          <div className='dropdown-items-container'>
            {items.map((item, itemIndex) => (
              <a
                key={`${id}-item-${itemIndex}`}
                className='dropdown-item'
                role='menuitem'
                href={item.href}
              >
                {item.text}
              </a>
            ))}
          </div>
        ) : (
          items.map((item, itemIndex) => (
            <a
              key={`${id}-item-${itemIndex}`}
              className='dropdown-item'
              role='menuitem'
              href={item.href}
            >
              {item.text}
            </a>
          ))
        )}
      </div>
    </div>
  );
};

/**
 * DropdownMenuContainer component that manages multiple dropdown menus
 * This can be used to render all dropdown menus defined in links.ts
 */
export interface DropdownMenuContainerProps {
  label: string;
  menuClassName?: string;
  menuId?: string;
  items?: DropdownItem[];
  /** Resolved section landing URL (e.g. `/portfolio` or `/es/portfolio`). Falls back to deriving from `items`. */
  triggerHref?: string;
  triggerActive?: boolean;
  tabIndex?: number;
  locale?: 'en' | 'es';
}

const DropdownMenuContainer: React.FC<DropdownMenuContainerProps> = ({
  label,
  menuClassName,
  menuId,
  items,
  triggerHref: triggerHrefProp,
  triggerActive,
  tabIndex,
  locale = 'en',
}) => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const menuEls = React.useRef<Array<HTMLDivElement | null>>([]);

  const manualItems = Array.isArray(items) ? items.filter((item) => item && item.href) : [];

  // For header integrations we can pass explicit items; otherwise keep legacy data-driven behavior.
  const filteredMenus: DropdownMenuType[] =
    manualItems.length > 0
      ? [
          {
            id: menuId || label.toLowerCase().replace(/\s+/g, '-'),
            triggerText: label,
            items: manualItems,
            isWide: (menuClassName || '').includes('wide'),
          },
        ]
      : dropdownMenus.filter((menu) => {
          const expectedClass = `dropdown-menu${menu.isWide ? '-wide' : ''}`;
          return menuClassName === expectedClass;
        });

  const toggleDropdown = (id: string) => {
    setOpenDropdown(openDropdown === id ? null : id);
  };

  React.useEffect(() => {
    if (!openDropdown) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      const inside = menuEls.current.some((el) => el && el.contains(target));
      if (!inside) setOpenDropdown(null);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openDropdown]);

  return (
    <>
      {filteredMenus.map((menu: DropdownMenuType, index) => {
        const triggerHref = triggerHrefProp ?? deriveTriggerHref(menu.items);
        return (
          <DropdownMenu
            key={menu.id}
            id={menu.id}
            triggerText={label}
            triggerHref={triggerHref}
            triggerActive={triggerActive}
            tabIndex={tabIndex}
            locale={locale}
            items={menu.items}
            menuClassName={menuClassName || `dropdown-menu${menu.isWide ? '-wide' : ''}`}
            isOpen={openDropdown === menu.id}
            onToggle={() => toggleDropdown(menu.id)}
            rootRef={(el) => {
              menuEls.current[index] = el;
            }}
          />
        );
      })}
    </>
  );
};

export { DropdownMenuContainer };
export default DropdownMenu;
